import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, Package, Truck, PackageCheck, ShoppingCart, Inbox, X, CheckCircle2 } from "lucide-react";

const STAGES = [
  { id: "nuovo", label: "Ricevuto", icon: Inbox },
  { id: "in_lavorazione", label: "In lavorazione", icon: Package },
  { id: "in_arrivo", label: "In spedizione", icon: Truck },
  { id: "consegnato", label: "Consegnato", icon: PackageCheck },
];

export default function PublicTracking() {
  const { quoteId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/public/tracking/${quoteId}`);
        setData(r.data);
      } catch (e) { setError(e.response?.data?.detail || e.message); }
      finally { setLoading(false); }
    })();
  }, [quoteId]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin" /></div>;
  if (error) return <div className="min-h-screen flex items-center justify-center text-red-600">Tracking non trovato</div>;

  const stato = data.stato || "nuovo";
  const currentIdx = STAGES.findIndex((s) => s.id === stato);
  const isAnnullato = stato === "annullato";

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="public-tracking-page">
      <div className="max-w-2xl mx-auto px-4 py-12">
        <div className="bg-white rounded-sm border border-border p-8 shadow-sm">
          <div className="overline text-brand mb-2">Tracking ordine</div>
          <h1 className="font-heading font-bold text-3xl mb-2">{data.sku_madre}</h1>
          <p className="text-sm text-muted-foreground mb-8">
            Salve {data.nome_cliente || "cliente"}, ecco lo stato della tua configurazione.
          </p>

          {isAnnullato ? (
            <div className="border border-red-200 bg-red-50 rounded-sm p-6 text-center">
              <X className="w-10 h-10 text-red-600 mx-auto mb-2" />
              <h3 className="font-bold text-red-900">Prenotazione annullata</h3>
              <p className="text-xs text-red-700 mt-1">Contattaci per maggiori informazioni.</p>
            </div>
          ) : (
            <>
              <div className="relative">
                <div className="absolute top-5 left-5 right-5 h-0.5 bg-border">
                  <div className="absolute top-0 left-0 h-full bg-brand transition-all"
                       style={{ width: `${Math.max(0, (currentIdx / (STAGES.length - 1)) * 100)}%` }} />
                </div>
                <div className="relative flex justify-between">
                  {STAGES.map((s, i) => {
                    const isDone = i <= currentIdx;
                    const isCurrent = i === currentIdx;
                    return (
                      <div key={s.id} className="flex flex-col items-center w-1/4" data-testid={`tracking-stage-${s.id}`}>
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${isDone ? "bg-brand border-brand text-white" : "bg-white border-border text-muted-foreground"} ${isCurrent ? "ring-4 ring-brand/20 animate-pulse" : ""}`}>
                          {isDone ? <CheckCircle2 className="w-5 h-5" /> : <s.icon className="w-5 h-5" />}
                        </div>
                        <div className={`text-[11px] mt-2 text-center ${isDone ? "font-medium" : "text-muted-foreground"}`}>{s.label}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          {data.tracking_code && (
            <div className="mt-8 p-4 bg-neutral-50 border border-border rounded-sm">
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Codice spedizione</div>
              <div className="font-mono font-medium text-sm" data-testid="tracking-code">{data.tracking_code}</div>
              {data.tracking_url && (
                <a href={data.tracking_url} target="_blank" rel="noopener noreferrer"
                  className="mt-2 inline-flex items-center gap-1.5 text-xs text-brand hover:underline" data-testid="tracking-url">
                  Verifica sul sito del corriere →
                </a>
              )}
            </div>
          )}

          <div className="mt-8 pt-6 border-t border-border">
            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Configurazione</div>
            <div className="space-y-1 text-sm">
              {data.selezioni?.tessuto?.codice && <div>🧶 Tessuto: <strong>{data.selezioni.tessuto.codice}</strong> {data.selezioni.tessuto.codice_colore}</div>}
              {data.selezioni?.piede?.codice && <div>🦵 Piede: <strong>{data.selezioni.piede.codice}</strong></div>}
              {data.selezioni?.metallo?.codice && <div>✨ Metallo: <strong>{data.selezioni.metallo.codice}</strong></div>}
            </div>
            {data.prezzo_stimato > 0 && (
              <div className="mt-3 text-lg font-bold">€ {data.prezzo_stimato.toFixed(2)}</div>
            )}
          </div>

          <div className="mt-6 text-[11px] text-muted-foreground text-center">
            Prenotazione del {(data.created_at || "").slice(0, 10)} · ID #{data.id.slice(0, 8)}
          </div>
        </div>
      </div>
    </div>
  );
}
